import React, { useEffect, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import T from 'prop-types'
import { format } from 'date-fns'
import itLocale from 'date-fns/locale/it'
import { Box, CircularProgress, Button, Typography } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import { useTranslation } from 'react-i18next'
import theme from 'theme'
import { mapLocation } from 'lib/carDetailsLib'
import { isRepairSession } from 'lib/sessionLifecycle'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'

import PageLoading from 'components/atoms/PageLoading'
import SearchField from 'components/atoms/SearchField'
import ConfirmBar from 'components/atoms/ConfirmBar'
import DownloadBar from 'components/atoms/DownloadBar'
import CarFiltersColumn from 'components/atoms/CarsFiltersColumn'
import CarCard from 'components/atoms/CarCard'
import AppealingIndex from 'components/atoms/AppealingIndex'
import ButtonLink from 'components/atoms/ButtonLink'
import CarsList from 'components/atoms/CarsList'
import ConfirmationDialog from 'components/atoms/ConfirmationDialog'
import LogIcon from 'components/atoms/LogIcon'

import useCarsFilters from 'hooks/useCarsFilters'
import useConfirmationDialog from 'hooks/useConfirmationDialog'
import useLogs from 'hooks/useLogs'
import useInputOutput from 'hooks/useInputOutput'

import CarPrices from '../Sessions/CarPrices'
import SessionsOption from '../Sessions/SessionsOption'
import useSessionChangePrice from '../Sessions/useSessionChangePrice'
import { GROUPS_TOTAL } from '../Sessions/sessionsLib'
import useCars from '../Sessions/useCars'

import CarDetails from './CarDetails'

export default function SessionsConfirmation({
  session,
  handleValidation,
  handleConfirm,
  updatePlatePricing,
  confirmed,
  readOnly,
  contentIsReadOnly = false,
  setShowHeader,
  debounceTime = 100,
  handleAdditionalInfoSync,
}) {
  const { t } = useTranslation()
  const repairSession = isRepairSession(session)

  const [filteredCars, setFilteredCars] = useState([])

  const { saveLogs, isFetching: isFetchingLogs } = useLogs()

  const { isFetching, fetchCars, cars } = useCars()
  const [showDetails, setShowDetails] = useState()

  useEffect(() => {
    setShowHeader(!showDetails)
  }, [setShowHeader, showDetails])

  useEffect(() => {
    fetchCars(session.sessionId)
  }, [fetchCars, session])

  const {
    edit,
    prices,
    initChangePrice,
    toggleChangePrice,
    toggleEditGroupPrice,
    setGroupPrice,
  } = useSessionChangePrice()

  useEffect(() => {
    if (!cars.length) {
      return
    }
    initChangePrice(cars, session.requiredGroups)
  }, [cars, initChangePrice, session.requiredGroups])

  const {
    filters,
    filtersData,
    initFilters,
    setKmRange,
    setDamageRange,
    setYearRange,
    setBrands,
    setModels,
    setLocations,
    setFuels,
    setCategory,
    setQuery,
    filterCars,
    resetFilters,
  } = useCarsFilters()

  useEffect(() => {
    if (!cars.length) {
      return
    }

    initFilters(cars)
  }, [fetchCars, cars, initFilters])

  const handleSetFilteredCars = useDebouncedCallback(
    (cars, filters, setFilteredCars, filterCars) =>
      setFilteredCars(filterCars(cars, filters)),
    debounceTime
  )

  useEffect(() => {
    handleSetFilteredCars(cars, filters, setFilteredCars, filterCars)
  }, [cars, handleSetFilteredCars, filters, setFilteredCars, filterCars])

  const handleChangeSearch = (event) => {
    setQuery(event.target.value)
  }

  const { saveXls } = useInputOutput()

  const handleDownload = async () => {
    const titlesRow = [t('common.excel.plate'), t('common.excel.reservePrice')]

    for (let i = 1; i <= GROUPS_TOTAL; i++) {
      titlesRow.push(
        t('sessions.group', {
          attribute: i,
        })
      )
    }
    titlesRow.push(
      t('common.excel.name'),
      t('carDetails.km'),
      t('carDetails.location'),
      t('carDetails.transmission'),
      t('carDetails.damage'),
      t('carDetails.year'),
      t('carDetails.fuelType'),
      t('carDetails.optionals')
    )

    const rows = [
      titlesRow,
      ...cars.map((car) => [
        car.plate,
        ...car.prices.map((price, i) =>
          i === 0 || session.requiredGroups.includes(i) ? price : null
        ),
        `${car.title}`,
        car.km,
        mapLocation(car.location),
        t(`carDetails.mapping.${car.transmission}`),
        car.reconditioningEstCost || '-',
        car.year,
        car.fuelType,
        Math.round(car.optionAmount) || '-',
      ]),
    ]

    saveXls('confirmation', rows)
  }

  const handleSavePricing = (plate, prices) => {
    updatePlatePricing(session.sessionId, plate, prices)
  }

  const {
    openConfirmationDialog,
    handleConfirmConfirmationDialog,
    handleOpenConfirmationDialog,
    handleCloseConfirmationDialog,
  } = useConfirmationDialog(handleConfirm)

  if (showDetails) {
    return (
      <CarDetails
        cars={filteredCars}
        session={session}
        isFetching={isFetching}
        isFetchingSession={isFetching}
        plate={showDetails}
        updatePlatePricing={updatePlatePricing}
        edit={edit}
        prices={prices}
        toggleChangePrice={toggleChangePrice}
        toggleEditGroupPrice={toggleEditGroupPrice}
        setGroupPrice={setGroupPrice}
        setShowDetails={setShowDetails}
        contentIsReadOnly={contentIsReadOnly}
      />
    )
  }

  return (
    <div data-testid="session-confirmation">
      <Box mt={1} />
      <ConfirmBar
        label={t(
          repairSession
            ? 'sessions.confirmation.repair.confirmButton'
            : 'sessions.confirmation.confirmButton'
        )}
        left={
          <SearchField
            data-testid="text-search"
            placeholder={t('common.form.searchBy')}
            value={filters.query}
            onChange={handleChangeSearch}
            sx={session.allFilesUploaded ? {} : { width: 0.8 }}
          />
        }
        leftProps={{ sx: { width: 0.5 } }}
        right={
          <>
            <SessionsOption
              label={t('sessions.options.titles.choice')}
              value={t(
                `sessions.options.${session.showOtherPartners ? 'on' : 'off'}`
              )}
            />
            <Box pr={2} />
            <Collapse orientation="horizontal" in={isFetchingLogs}>
              <div style={{ marginLeft: 4, marginRight: 4 }}>
                <CircularProgress size={20} />
              </div>
            </Collapse>
            <LogIcon
              disabled={isFetchingLogs}
              handleClick={() =>
                saveLogs(`logs_sessions_${session?.sessionId}`, {
                  area: t('common.logs.sessions'),
                  sessionId: session?.sessionId,
                })
              }
            />
            <DownloadBar handleDownload={handleDownload} />

            {!session.confirmationAcceptedAt && (
              <Button
                data-testid="missing-files-button"
                color="error"
                variant="text"
                disabled={confirmed || readOnly || contentIsReadOnly}
                onClick={handleAdditionalInfoSync}
                sx={{
                  marginLeft: (theme) => theme.spacing(2),
                  display: session.allFilesUploaded ? 'none' : 'flex',
                  width: 0.6,
                }}
              >
                <WarningAmberRoundedIcon
                  color="secondary"
                  sx={{
                    bgcolor: readOnly ? 'lightgrey' : 'error.main',
                    borderRadius: 1,
                  }}
                />
                <Box pr={1} />
                <Typography>
                  {t('sessions.confirmation.fileUpload.notAllFilesUploaded')}
                </Typography>
              </Button>
            )}
          </>
        }
        disabled={
          !handleValidation() || confirmed || readOnly || contentIsReadOnly
        }
        handleConfirm={handleOpenConfirmationDialog}
        sticky
      />
      <CarsList
        left={
          <CarFiltersColumn
            filters={filters}
            filtersData={filtersData}
            filteredCars={
              filteredCars?.length !== cars.length ? filteredCars : undefined
            }
            setKmRange={setKmRange}
            setDamageRange={setDamageRange}
            setYearRange={setYearRange}
            setBrands={setBrands}
            setModels={setModels}
            setLocations={setLocations}
            setFuels={setFuels}
            setCategory={setCategory}
            resetFilters={resetFilters}
          />
        }
        right={
          <>
            {!isFetching ? (
              filteredCars.map((car, index) => (
                <CarCard
                  data-testid={`car-${index}`}
                  key={car.originalId}
                  onClick={() => setShowDetails(car.plate)}
                  height={theme.custom.carCardAdditionalMaxHeight}
                  car={car}
                  right={
                    <>
                      <AppealingIndex value={car.appealing} />
                      <ButtonLink
                        data-testid={`button-toggle-pricing-${index}`}
                        disabled={readOnly || contentIsReadOnly}
                        onClick={(e) => {
                          toggleChangePrice(car.plate, handleSavePricing)
                          e.preventDefault()
                        }}
                      >
                        <strong>
                          {t(
                            `sessions.${
                              !edit[car.plate] ? 'change' : 'save'
                            }Price`
                          )}
                        </strong>
                      </ButtonLink>
                    </>
                  }
                  bottom={
                    <CarPrices
                      plate={car.plate}
                      prices={prices[car.plate]}
                      handleChangeGroupPrice={setGroupPrice}
                      toggleEditGroupPrice={toggleEditGroupPrice}
                      edit={edit[car.plate]}
                      readOnly={readOnly || contentIsReadOnly}
                    />
                  }
                />
              ))
            ) : (
              <PageLoading />
            )}
          </>
        }
      />
      <ConfirmationDialog
        title={t(
          repairSession
            ? 'sessions.confirmation.repair.confirmationDialog.title'
            : 'sessions.confirmation.confirmationDialog.title'
        )}
        content={
          <>
            {t(
              repairSession
                ? 'sessions.confirmation.repair.confirmationDialog.content'
                : 'sessions.confirmation.confirmationDialog.content'
            )}
            <strong>
              {session.agenda?.publication.startsAt &&
                format(
                  new Date(session.agenda.publication.startsAt),
                  t('common.date.format'),
                  {
                    locale: itLocale,
                  }
                )}
            </strong>
          </>
        }
        open={openConfirmationDialog}
        handleClose={handleCloseConfirmationDialog}
        handleSubmit={handleConfirmConfirmationDialog}
      />
    </div>
  )
}

SessionsConfirmation.propTypes = {
  session: T.object.isRequired,
  handleValidation: T.func.isRequired,
  handleConfirm: T.func.isRequired,
  updatePlatePricing: T.func.isRequired,
  confirmed: T.bool,
  readOnly: T.bool,
  contentIsReadOnly: T.bool,
  handleAdditionalInfoSync: T.func.isRequired,
}
